import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isInAppBrowser } from '../utils/browserDetection';
import { AuthLayout, GoogleSignInButton, MicrosoftSignInButton, OrDivider } from '../components/auth';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { startOAuth as beginOAuth } from '../utils/oauthUtils';

/**
 * Signup Page - identifier-first sign-up
 *
 * Collects ONLY an email address (plus the OAuth buttons). Submitting the
 * email navigates to /signup/continue, which checks the account status and
 * either sends the verification email or routes an existing account to the
 * right sign-in method.
 */

const Signup: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'microsoft' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { trackGoogleSignup, trackMicrosoftSignup, trackEmailSignup } = useAnalyticsTracking();
  const [searchParams] = useSearchParams();
  const [detectedInAppBrowser] = useState(() => isInAppBrowser());

  const [email, setEmail] = useState<string>(() => (location.state as any)?.email || '');

  // Capture plan selection from pricing page URL (?plan=standard)
  // Store in sessionStorage so it persists through OAuth redirect
  useEffect(() => {
    const planSlug = searchParams.get('plan');
    if (planSlug) {
      sessionStorage.setItem('selectedPlanSlug', planSlug);
    }
  }, [searchParams]);

  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    // Track email signup submission
    trackEmailSignup();

    navigate('/signup/continue', { state: { email: trimmed } });
  };

  const startOAuth = (provider: 'google' | 'microsoft') => {
    setError(null);
    setIsLoading(true);
    setLoadingProvider(provider);

    if (provider === 'google') trackGoogleSignup();
    else trackMicrosoftSignup();

    try {
      // signup mode: force consent screen to obtain a calendar refresh token
      beginOAuth(provider, 'signup');
    } catch (err) {
      setError(t('common:errors.connectionFailed', 'Failed to connect. Please try again.'));
      setIsLoading(false);
      setLoadingProvider(null);
    }
  };

  return (
    <AuthLayout
      title={t('common:auth.getStartedFree', 'Get started for free')}
      subtitle={t('common:auth.signUpSubtitle', 'Create your account and start scheduling in minutes')}
      mode="signup"
      error={error}
      isInAppBrowser={detectedInAppBrowser}
    >
      <form onSubmit={handleEmailContinue} className="space-y-4" noValidate>
        <div>
          <label htmlFor="signup-email" className="form-label">
            {t('common:auth.emailLabel', 'Email address')}
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-base w-full"
            disabled={isLoading}
          />
        </div>
        <button type="submit" className="btn-primary btn-lg w-full" disabled={isLoading || !email.trim()}>
          {t('common:actions.continue', 'Continue')}
        </button>
      </form>

      <OrDivider />

      <GoogleSignInButton
        onClick={(e) => { e.preventDefault(); startOAuth('google'); }}
        isLoading={loadingProvider === 'google'}
        disabled={isLoading}
        mode="signup"
      />
      <MicrosoftSignInButton
        onClick={(e) => { e.preventDefault(); startOAuth('microsoft'); }}
        isLoading={loadingProvider === 'microsoft'}
        disabled={isLoading}
        mode="signup"
      />
    </AuthLayout>
  );
};

export default Signup;
